import React, { useState, useEffect, useCallback, useRef } from 'react';
import JSZip from 'jszip';
import { useAuth } from '../Auth/AuthContext';
import { apiCall } from '../../utils/apiUtils';
import { placeOrder as apiPlaceOrder, listOrders as apiListOrders } from '../../services/liveStamping';
import { showToast } from '../../utils/toastUtils';
import { formatDateTimeIST, formatISTDateOnly } from '../../utils/dateUtils';
import LiveStampingServiceConfig from './LiveStampingServiceConfig';
import LiveStampingUploadModal from './LiveStampingUploadModal';
import LiveStampingOrderModal from './LiveStampingOrderModal';
import LiveStampingServiceDetailModal from './LiveStampingServiceDetailModal';
import LiveStampingRequestModal from './LiveStampingRequestModal';
import LiveStampingDepositModal from './LiveStampingDepositModal';
import LiveStampingPaymentJournal from './LiveStampingPaymentJournal';
import VendorSampleUpload from './VendorSampleUpload';
import Modal from '../Common/Modal';
import CopyableText from '../Common/CopyableText';

// ============================================================================
// API BASE URL
// ============================================================================
const API_BASE = '/api/external/v1/live-stamping';

// ============================================================================
// ADMIN REVIEW MODAL
// ============================================================================

const DetailRow = ({ label, value }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = () => {
        if (!value) return;
        navigator.clipboard.writeText(String(value)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <tr className="even:bg-gray-50">
            <td className="px-4 py-2 font-medium text-gray-600 w-2/5 whitespace-nowrap">{label}</td>
            <td className="px-4 py-2 text-gray-900 flex items-center justify-between gap-2">
                <span>{value || <span className="text-gray-400 italic">—</span>}</span>
                {value && (
                    <button
                        onClick={handleCopy}
                        title="Copy"
                        className="shrink-0 text-gray-400 hover:text-indigo-600 transition-colors"
                    >
                        {copied ? (
                            <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 10h6a2 2 0 002-2v-8a2 2 0 00-2-2h-6a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        )}
                    </button>
                )}
            </td>
        </tr>
    );
};

const AdminReviewModal = ({ order, onClose, onApprove, onReject }) => {
    const [fullOrder, setFullOrder] = useState(order);
    const [loading, setLoading] = useState(true);
    const [rejectReason, setRejectReason] = useState('');
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!order?.id) return;
        import('../../services/liveStamping').then(({ getOrder }) => {
            getOrder(order.id)
                .then(res => setFullOrder(res.order || res))
                .catch(() => {})
                .finally(() => setLoading(false));
        });
    }, [order?.id]);

    const isPreviewStage = ['preview_submitted', 'metadata_ready'].includes(order.status);
    const isFinalStage = ['compliance_check', 'pending_verification'].includes(order.status);
    const isReadOnly = order.status === 'metadata_ready'; // preview already approved, just viewing

    const handleApprove = async () => {
        setSubmitting(true);
        await onApprove(fullOrder);
        setSubmitting(false);
    };

    const handleReject = async () => {
        setSubmitting(true);
        await onReject(fullOrder.id, rejectReason);
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b flex items-center justify-between bg-white flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">
                            {isReadOnly ? 'Approved Preview' : isPreviewStage ? 'Review Preview Sample' : 'Review Certificate & Document'}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Order <span className="font-medium text-indigo-600">#{fullOrder.id}</span>
                            &nbsp;&bull;&nbsp;{fullOrder.service_name || fullOrder.serviceName}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* ── Certificate details (on top, with copy buttons) ── */}
                            {(isPreviewStage || isFinalStage) && fullOrder.stamp_metadata && (
                                <div className="border border-gray-200 rounded-xl overflow-hidden text-sm">
                                    <table className="w-full">
                                        <tbody className="divide-y divide-gray-100">
                                            {fullOrder.stamp_metadata.certificate_number && (
                                                <DetailRow label="Certificate ID" value={fullOrder.stamp_metadata.certificate_number} />
                                            )}
                                            {fullOrder.stamp_metadata.issue_date && (
                                                <DetailRow label="Issue date" value={formatDateTimeIST(fullOrder.stamp_metadata.issue_date)} />
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ── Media (below details) ── */}
                            {isPreviewStage && fullOrder.preview_base64 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Uploaded Preview</h3>
                                    {(fullOrder.preview_content_type || '').includes('pdf') ? (
                                        <iframe
                                            src={`data:application/pdf;base64,${fullOrder.preview_base64}`}
                                            className="w-full h-[420px] rounded-lg border border-gray-200"
                                            title="Preview PDF"
                                        />
                                    ) : (
                                        <img
                                            src={`data:${fullOrder.preview_content_type || 'image/png'};base64,${fullOrder.preview_base64}`}
                                            alt="Preview"
                                            className="max-w-full rounded-lg border border-gray-200 shadow-sm"
                                        />
                                    )}
                                </div>
                            )}
                            {isPreviewStage && !fullOrder.preview_base64 && !loading && (
                                <div className="text-center py-8 text-gray-400 italic text-sm">No preview file found on this order.</div>
                            )}
                            {isFinalStage && fullOrder.document_base64 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Uploaded Document</h3>
                                    {(fullOrder.document_content_type || 'application/pdf').includes('pdf') ? (
                                        <iframe
                                            src={`data:application/pdf;base64,${fullOrder.document_base64}`}
                                            className="w-full h-[420px] rounded-lg border border-gray-200"
                                            title="Final Document"
                                        />
                                    ) : (
                                        <img
                                            src={`data:${fullOrder.document_content_type};base64,${fullOrder.document_base64}`}
                                            alt="Document"
                                            className="max-w-full rounded-lg border border-gray-200 shadow-sm"
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* Reject reason form */}
                    {showRejectForm && (
                        <div className={`${isPreviewStage ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'} border rounded-xl p-4 space-y-3`}>
                            <p className={`text-sm font-semibold ${isPreviewStage ? 'text-amber-700' : 'text-red-700'}`}>
                                {isPreviewStage ? 'Reason for re-upload request' : 'Rejection Reason'}
                            </p>
                            <textarea
                                className={`w-full border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 ${isPreviewStage ? 'border-amber-300 focus:ring-amber-400' : 'border-red-300 focus:ring-red-400'}`}
                                rows="3"
                                placeholder={isPreviewStage
                                    ? 'Describe what the vendor needs to fix and re-upload...'
                                    : 'Optional — describe why this is being rejected...'}
                                value={rejectReason}
                                onChange={e => setRejectReason(e.target.value)}
                            />
                            <div className="flex gap-2 justify-end">
                                <button onClick={() => setShowRejectForm(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                                <button
                                    onClick={handleReject}
                                    disabled={submitting}
                                    className={`px-4 py-1.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 ${isPreviewStage ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
                                >
                                    {submitting
                                        ? (isPreviewStage ? 'Sending back…' : 'Rejecting…')
                                        : (isPreviewStage ? 'Send back for re-upload' : 'Confirm Reject')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                {!showRejectForm && (
                    <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3 flex-shrink-0">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Close</button>
                        {!isReadOnly && (
                            <>
                                <button
                                    onClick={() => setShowRejectForm(true)}
                                    disabled={submitting || loading}
                                    className={`px-4 py-2 text-sm font-semibold border rounded-lg disabled:opacity-50 ${isPreviewStage ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100' : 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100'}`}
                                >
                                    {isPreviewStage ? 'Send Back' : 'Reject'}
                                </button>
                                <button
                                    onClick={handleApprove}
                                    disabled={submitting || loading}
                                    className="px-5 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Approving…' : isPreviewStage ? 'Approve Preview' : 'Approve & Complete'}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// MAIN PANEL COMPONENT
// ============================================================================
const LiveStampingPanel = () => {
    const { user } = useAuth();

    const [services, setServices] = useState([]);
    const [orders, setOrders] = useState([]);
    const [paymentRequests, setPaymentRequests] = useState([]);
    const [walletBalance, setWalletBalance] = useState(0);
    const [lastTransaction, setLastTransaction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const emptyFilters = { status: '', from_date: '', to_date: '', external_ref_id: '', source_csv_upload_id: '' };

    // Services table filters/search/sort (client view)
    const [svcSearch, setSvcSearch] = useState('');
    const [svcStateFilter, setSvcStateFilter] = useState('');
    const [svcSort, setSvcSort] = useState('name_asc'); // 'name_asc'|'name_desc'|'price_asc'|'price_desc'
    const [svcActiveOnly, setSvcActiveOnly] = useState(true); // default: show only active
    const [svcPage, setSvcPage] = useState(1);
    const SVC_PAGE_SIZE = 6;
    const [svcGoToPage, setSvcGoToPage] = useState('');
    const [orderFilters, setOrderFilters] = useState(emptyFilters);
    const [pendingFilters, setPendingFilters] = useState(emptyFilters);

    // Pagination state
    const [servicesPagination, setServicesPagination] = useState({ offset: 0, limit: 200, total: 0, hasMore: true, loading: false });
    const [ordersPagination, setOrdersPagination] = useState({ page: 1, limit: 15, total: 0 });
    const [orderStatusCounts, setOrderStatusCounts] = useState({});
    const [requestsPagination, setRequestsPagination] = useState({ page: 1, limit: 15, total: 0 });

    // Modal states
    const [selectedService, setSelectedService] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [uploadOrder, setUploadOrder] = useState(null);
    const [previewUploadOrder, setPreviewUploadOrder] = useState(null);
    const [requestService, setRequestService] = useState(null);
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [showTransactionsBlade, setShowTransactionsBlade] = useState(false);
    const [showBalanceMenu, setShowBalanceMenu] = useState(false);
    const [uploadCsvService, setUploadCsvService] = useState(null);
    const [uploadCsvFile, setUploadCsvFile] = useState(null);
    const [uploadCsvPreviewRows, setUploadCsvPreviewRows] = useState(null);
    const [uploadCsvPreviewHeaders, setUploadCsvPreviewHeaders] = useState([]);
    const [uploadCsvError, setUploadCsvError] = useState('');
    const [uploadCsvSubmitting, setUploadCsvSubmitting] = useState(false);
    const [uploadCsvDragging, setUploadCsvDragging] = useState(false);
    const [uploadCsvResult, setUploadCsvResult] = useState(null);
    const [uploadCsvRawText, setUploadCsvRawText] = useState('');
    const [viewCsvService, setViewCsvService] = useState(null);
    const [serviceCsvUploads, setServiceCsvUploads] = useState([]);
    const [serviceCsvUploadsLoading, setServiceCsvUploadsLoading] = useState(false);
    const [serviceCsvUploadsError, setServiceCsvUploadsError] = useState('');
    const [activeCsvFilter, setActiveCsvFilter] = useState(null);
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [reviewOrder, setReviewOrder] = useState(null);
    const [svcTableCollapsed, setSvcTableCollapsed] = useState(false);
    const [pinnedServiceIds, setPinnedServiceIds] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem('ls_pinnedServices') || '[]')); }
        catch { return new Set(); }
    });
    const [recentServiceIds, setRecentServiceIds] = useState(() => {
        try { return JSON.parse(localStorage.getItem('ls_recentServices') || '[]'); }
        catch { return []; }
    });
    const [orderSearch, setOrderSearch] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [serviceFilter, setServiceFilter] = useState('');
    const [goToPageInput, setGoToPageInput] = useState('');
    const [showExportMenu, setShowExportMenu] = useState(false);

    const exportMenuRef = useRef(null);
    const balanceMenuRef = useRef(null);

    useEffect(() => {
        if (!showExportMenu && !showBalanceMenu) return;
        const handleClickOutside = (e) => {
            if (showExportMenu && exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setShowExportMenu(false);
            }
            if (showBalanceMenu && balanceMenuRef.current && !balanceMenuRef.current.contains(e.target)) {
                setShowBalanceMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showExportMenu, showBalanceMenu]);

    // Scroll sentinel ref for infinite scroll
    const servicesScrollRef = useRef(null);

    // Fetch services with pagination (for infinite scroll)
    const fetchServices = useCallback(async (reset = false) => {
        if (servicesPagination.loading) return;

        const currentOffset = reset ? 0 : servicesPagination.offset;
        if (!reset && !servicesPagination.hasMore) return;

        setServicesPagination(prev => ({ ...prev, loading: true }));

        try {
            const url = `${API_BASE}/services?include_disabled=true&limit=${servicesPagination.limit}&offset=${currentOffset}`;
            const response = await apiCall(url, { method: 'GET' });

            if (response.success) {
                const mappedServices = (response.services || []).map(s => ({
                    id: s.id,
                    name: s.name,
                    state: s.state,
                    basePrice: s.base_price,
                    platformFee: s.platform_fee,
                    isEnabled: s.is_enabled,
                    stamps: s.stamps || [],
                    article: s.stamps?.[0]?.default_values?.article || '',
                    denomination: s.stamps?.[0]?.default_values?.denomination || 0,
                    fixedFields: s.stamps?.[0]?.locked_fields || {},
                    defaultValues: s.stamps?.[0]?.default_values || {},
                    assigned_vendor_id: s.assigned_vendor_id,
                    client_id: s.client_id,
                    client_name: s.client_name
                }));

                const newTotal = response.total || 0;
                const newOffset = currentOffset + mappedServices.length;

                setServices(prev => reset ? mappedServices : [...prev, ...mappedServices]);
                setServicesPagination(prev => ({
                    ...prev,
                    offset: newOffset,
                    total: newTotal,
                    hasMore: newOffset < newTotal,
                    loading: false
                }));
            }
        } catch (err) {
            console.error('Failed to fetch services:', err);
            setError('Failed to load services');
            setServicesPagination(prev => ({ ...prev, loading: false }));
        }
    }, [user?.role, servicesPagination.offset, servicesPagination.limit, servicesPagination.hasMore, servicesPagination.loading]);

    const normalizeOrderFilters = (filters) => {
        const normalized = { ...filters };
        if (normalized.to_date && /^\d{4}-\d{2}-\d{2}$/.test(normalized.to_date)) {
            normalized.to_date = `${normalized.to_date}T23:59:59`;
        }
        return normalized;
    };

    // Fetch orders with pagination and filters
    const fetchOrders = useCallback(async (page = 1, filters = orderFilters, search = orderSearch, clientF = clientFilter, serviceF = serviceFilter) => {
        try {
            const offset = (page - 1) * ordersPagination.limit;
            const normalizedFilters = normalizeOrderFilters(filters);
            const response = await apiListOrders({
                ...normalizedFilters,
                ...(search ? { search } : {}),
                ...(serviceF ? { service_name: serviceF } : {}),
                ...(clientF ? { client_name_filter: clientF } : {}),
                limit: ordersPagination.limit,
                offset
            });
            if (response.success) {
                setOrders(response.orders || []);
                setOrdersPagination(prev => ({ ...prev, page, total: response.total || 0 }));
                setOrderStatusCounts(response.status_counts || {});
            }
        } catch (err) {
            console.error('Failed to fetch orders:', err);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordersPagination.limit]);

    // Fetch wallet balance
    const fetchWallet = useCallback(async () => {
        try {
            const response = await apiCall(`${API_BASE}/wallet`, { method: 'GET' });
            if (response.balance !== undefined) {
                setWalletBalance(response.balance);
            }
            if (response.last_transaction) {
                setLastTransaction(response.last_transaction);
            }
        } catch (err) {
            console.error('Failed to fetch wallet:', err);
        }
    }, []);

    // Fetch Payment Requests with pagination
    const fetchPaymentRequests = useCallback(async (page = 1) => {
        try {
            const offset = (page - 1) * requestsPagination.limit;
            const response = await apiCall(`${API_BASE}/wallet/requests?limit=${requestsPagination.limit}&offset=${offset}`, { method: 'GET' });
            if (response.success) {
                setPaymentRequests(response.requests || []);
                setRequestsPagination(prev => ({ ...prev, page, total: response.total || 0 }));
            }
        } catch (err) {
            console.error('Failed to fetch payment requests:', err);
        }
    }, [requestsPagination.limit]);

    // Initial data load
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const promises = [fetchOrders()];

            if (user.role === 'client') {
                promises.push(fetchWallet());
                promises.push(fetchServices(true)); // reset=true for initial load
                promises.push(fetchPaymentRequests());
            } else if (user.role === 'admin') {
                promises.push(fetchServices(true)); // reset=true for initial load
                promises.push(fetchPaymentRequests());
            } else if (user.role === 'vendor') {
                // Vendors see assigned orders
            }

            await Promise.all(promises);
            setLoading(false);
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.role]); // Only trigger on role change, not on every function recreation

    // Re-fetch when dropdown filters change (server-side)
    useEffect(() => {
        fetchOrders(1, orderFilters, orderSearch, clientFilter, serviceFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientFilter, serviceFilter]);

    // Debounced re-fetch for search input (server-side)
    useEffect(() => {
        const t = setTimeout(() => {
            fetchOrders(1, orderFilters, orderSearch, clientFilter, serviceFilter);
        }, 350);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderSearch]);

    // Close currently open panel/modal on Escape
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key !== 'Escape') return;

            if (showTransactionsBlade) {
                setShowTransactionsBlade(false);
                return;
            }
            if (uploadCsvService) {
                setUploadCsvService(null);
                return;
            }
            if (viewCsvService) {
                setViewCsvService(null);
                return;
            }
            if (requestService) {
                setRequestService(null);
                return;
            }
            if (showDepositModal) {
                setShowDepositModal(false);
                return;
            }
            if (selectedService) {
                setSelectedService(null);
                return;
            }
            if (selectedOrder) {
                setSelectedOrder(null);
                return;
            }
            if (uploadOrder) {
                setUploadOrder(null);
                return;
            }
            if (previewUploadOrder) {
                setPreviewUploadOrder(null);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [
        showTransactionsBlade,
        uploadCsvService,
        viewCsvService,
        requestService,
        showDepositModal,
        selectedService,
        selectedOrder,
        uploadOrder,
        previewUploadOrder
    ]);

    // IntersectionObserver for infinite scroll on services
    useEffect(() => {
        if (!servicesScrollRef.current) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && servicesPagination.hasMore && !servicesPagination.loading) {
                    fetchServices(false);
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(servicesScrollRef.current);
        return () => observer.disconnect();
    }, [servicesPagination.hasMore, servicesPagination.loading, fetchServices]);

    const visibleOrders = orders;

    // Status Color Helper
    const getStatusColor = (status) => {
        const colors = {
            placed: 'bg-blue-100 text-blue-800',
            executing: 'bg-orange-100 text-orange-800',
            preview_submitted: 'bg-violet-100 text-violet-800',
            metadata_ready: 'bg-teal-100 text-teal-800',
            compliance_check: 'bg-purple-100 text-purple-800',
            pending_verification: 'bg-purple-100 text-purple-800',
            completed: 'bg-green-100 text-green-800',
            ops_review: 'bg-yellow-100 text-yellow-800',
            rejected: 'bg-red-100 text-red-800',
            payment_pending: 'bg-pink-100 text-pink-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Admin: Approve Order (two-stage aware) — called from AdminReviewModal
    const handleApproveOrder = async (order) => {
        let endpoint;
        if (order.status === 'preview_submitted') {
            endpoint = `${API_BASE}/orders/${order.id}/approve-preview`;
        } else if (['compliance_check', 'pending_verification'].includes(order.status)) {
            endpoint = `${API_BASE}/orders/${order.id}/approve-final`;
        } else {
            endpoint = `${API_BASE}/orders/${order.id}/approve`;
        }
        try {
            const response = await apiCall(endpoint, { method: 'POST' });
            if (response.success) {
                setReviewOrder(null);
                await fetchOrders();
                showToast.success('Order approved successfully!');
            }
        } catch (err) {
            console.error(err);
            showToast.error(err.message || 'Failed to approve order');
        }
    };

    // Admin: Reject Order — called from AdminReviewModal
    const handleRejectOrder = async (orderId, reason = '') => {
        try {
            const response = await apiCall(`${API_BASE}/orders/${orderId}/reject`, {
                method: 'POST',
                body: JSON.stringify({ reason })
            });
            if (response.success) {
                setReviewOrder(null);
                await fetchOrders();
                if (response.new_status === 'executing') {
                    showToast.success('Preview sent back to vendor for re-upload.');
                } else {
                    showToast.success('Order rejected.');
                }
            }
        } catch (err) {
            console.error(err);
            showToast.error(err.message || 'Failed to reject order');
        }
    };

    // Client: Download Document
    const handleDownloadDocument = async (orderId) => {
        try {
            const response = await fetch(`${API_BASE}/orders/${orderId}/download`, {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Download failed');
            }
            // Get filename from Content-Disposition header
            const disposition = response.headers.get('Content-Disposition');
            let filename = `order_${orderId}.pdf`;
            if (disposition && disposition.includes('filename=')) {
                filename = disposition.split('filename=')[1].replace(/"/g, '');
            }
            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch (err) {
            console.error(err);
            alert(err.message || 'Failed to download document');
        }
    };

    // Actions
    const handleDepositSubmit = async (data) => {
        try {
            const response = await apiCall(`${API_BASE}/wallet/deposit`, {
                method: 'POST',
                body: JSON.stringify(data)
            });
            if (response.id) {
                setShowDepositModal(false);
                await fetchPaymentRequests(); // Refresh journal
                alert('Deposit request submitted successfully! Waiting for approval.');
            }
        } catch (err) {
            console.error('Failed to submit deposit:', err);
            alert('Failed to submit deposit request');
        }
    };

    const handleApproveRequest = async (id) => {
        if (!window.confirm("Approve this payment and credit funds?")) return;
        try {
            const response = await apiCall(`${API_BASE}/wallet/requests/${id}/approve`, { method: 'POST' });
            if (response.success) {
                await fetchPaymentRequests();
                await fetchWallet();
                await fetchOrders();
                alert('Payment approved and funds added.');
            }
        } catch (err) {
            console.error(err);
            alert(err.message || 'Approval failed');
        }
    };

    const handleRejectRequest = async (id) => {
        if (!window.confirm("Reject this payment request?")) return;
        try {
            const response = await apiCall(`${API_BASE}/wallet/requests/${id}/reject`, { method: 'POST' });
            if (response.success) {
                await fetchPaymentRequests();
                alert('Payment request rejected.');
            }
        } catch (err) {
            console.error(err);
            alert('Rejection failed');
        }
    };

    const handlePlaceOrder = async (orderData) => {
        try {
            const response = await apiPlaceOrder({
                service_id: orderData.service_id,
                stamp_data: orderData.stamp_data,
                consideration_amount: orderData.consideration_amount,
                delivery_mode: orderData.delivery_mode || 'digital',
                external_ref_id: orderData.external_ref_id,
            });

            if (response.success) {
                if (response.wallet_balance_after !== undefined) setWalletBalance(response.wallet_balance_after);
                await fetchOrders();
                setSelectedService(null);

                if (response.status === 'payment_pending') {
                    alert('Order Registered! Status: Payment Pending. Please add funds to process.');
                }
                return true;
            } else {
                alert(response.detail || 'Failed to place order');
                return false;
            }
        } catch (err) {
            console.error('Failed to place order:', err);
            if (err.code === 'INSUFFICIENT_BALANCE') {
                setSelectedService(null);
                setShowDepositModal(true);
                showToast('Insufficient balance — please add funds to continue.', 'error');
            } else {
                alert(err.message || 'Failed to place order');
            }
            return false;
        }
    };

    const toggleService = async (id) => {
        const service = services.find(s => s.id === id);
        if (!service) return;
        try {
            await apiCall(`${API_BASE}/services/${id}`, {
                method: 'PATCH',
                body: JSON.stringify({ enabled: !service.isEnabled })
            });
            setServices(services.map(s => s.id === id ? { ...s, isEnabled: !service.isEnabled } : s));
        } catch (err) { console.error(err); }
    };

    const addService = async (data) => {
        try {
            const res = await apiCall(`${API_BASE}/services`, { method: 'POST', body: JSON.stringify(data) });
            if (res.success) fetchServices(true);  // Reset to refresh full list
        } catch (err) { console.error(err); alert('Failed'); }
    };

    const updateService = async (id, data) => {
        try {
            const res = await apiCall(`${API_BASE}/services/${id}`, { method: 'PUT', body: JSON.stringify(data) });
            if (res.success) fetchServices(true);  // Reset to refresh full list
        } catch (err) { console.error(err); alert('Failed'); }
    };

    const deleteService = async (id) => {
        try {
            const res = await apiCall(`${API_BASE}/services/${id}`, { method: 'DELETE' });
            if (res.success) setServices(services.filter(s => s.id !== id));
        } catch (err) { console.error(err); alert('Failed'); }
    };

    const handleUploadComplete = async () => {
        fetchOrders();
        setUploadOrder(null);
    };

    const toggleOrderSelection = (orderId) => {
        setSelectedOrderIds(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) {
                next.delete(orderId);
            } else {
                next.add(orderId);
            }
            return next;
        });
    };

    const toggleSelectAllVisibleOrders = () => {
        const visibleIds = visibleOrders.map(o => o.id);
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedOrderIds.has(id));
        setSelectedOrderIds(prev => {
            const next = new Set(prev);
            if (allSelected) {
                visibleIds.forEach(id => next.delete(id));
            } else {
                visibleIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    useEffect(() => {
        const visibleIds = new Set(visibleOrders.map(o => o.id));
        setSelectedOrderIds(prev => {
            const next = new Set();
            prev.forEach(id => {
                if (visibleIds.has(id)) next.add(id);
            });
            return next;
        });
    }, [visibleOrders]);

    const formatDateTime = (val) => {
        if (!val) return '';
        const d = new Date(val);
        if (Number.isNaN(d.getTime())) return String(val);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    };

    const buildExportCsv = (ordersToExport) => {
        if (!ordersToExport.length) return '';

        const maxStamps = Math.max(...ordersToExport.map(o => (o.stamps || []).length), 1);
        const baseCols = [
            'order_id', 'external_ref_id', 'service_id', 'service_name', 'state',
            'status', 'delivery_mode', 'delivery_address',
            'consideration_amount', 'certificate_id', 'certificate_datetime',
            'order_notes', 'created_at', 'updated_at'
        ];
        const stampCols = [];
        for (let n = 1; n <= maxStamps; n++) {
            const p = `stamp_${n}_`;
            stampCols.push(
                `${p}article`, `${p}denomination`, `${p}quantity`,
                `${p}first_party_name`, `${p}first_party_mobile`, `${p}first_party_address`,
                `${p}second_party_name`, `${p}second_party_mobile`, `${p}second_party_address`,
                `${p}stamp_purchased_by`, `${p}stamp_paid_by`, `${p}property_description`
            );
        }

        const escapeCsv = (value) => {
            const str = value === null || value === undefined ? '' : String(value);
            if (str.includes('"') || str.includes(',') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const lines = [];
        lines.push([...baseCols, ...stampCols].map(escapeCsv).join(','));

        ordersToExport.forEach(order => {
            const base = [
                order.id || order.order_id || '',
                order.external_ref_id || '',
                order.service_id || '',
                order.service_name || order.serviceName || '',
                order.state || '',
                order.status || '',
                order.delivery_mode || '',
                order.delivery_address || '',
                order.consideration_amount || '',
                ['metadata_ready', 'compliance_check', 'pending_verification', 'completed'].includes(order.status) ? (order.stamp_metadata?.certificate_number || '') : '',
                ['metadata_ready', 'compliance_check', 'pending_verification', 'completed'].includes(order.status) && order.stamp_metadata?.issue_date ? formatDateTime(order.stamp_metadata.issue_date) : '',
                order.order_notes || '',
                formatDateTime(order.created_at),
                formatDateTime(order.updated_at)
            ];

            const stampVals = [];
            const stamps = order.stamps || [];
            for (let n = 1; n <= maxStamps; n++) {
                const s = stamps[n - 1] || {};
                const fp = s.first_party || {};
                const sp = s.second_party || {};
                stampVals.push(
                    s.article || '',
                    s.denomination || '',
                    s.quantity || '',
                    fp.name || '',
                    fp.mobile || '',
                    fp.address || '',
                    sp.name || '',
                    sp.mobile || '',
                    sp.address || '',
                    s.stamp_purchased_by || '',
                    s.stamp_paid_by || '',
                    s.notes || ''
                );
            }
            lines.push([...base, ...stampVals].map(escapeCsv).join(','));
        });
        return lines.join('\n');
    };

    const exportSelectedOrders = () => {
        const selectedOrders = visibleOrders.filter(o => selectedOrderIds.has(o.id));
        const exportOrders = selectedOrders.length ? selectedOrders : visibleOrders;
        if (!exportOrders.length) {
            showToast.error('No orders available to export');
            return;
        }
        const csvText = buildExportCsv(exportOrders);
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders_selected_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportZipWithPdfs = async () => {
        const selectedOrders = visibleOrders.filter(o => selectedOrderIds.has(o.id));
        const exportOrders = selectedOrders.length ? selectedOrders : visibleOrders;
        if (!exportOrders.length) {
            showToast.error('No orders to export');
            return;
        }
        const zip = new JSZip();
        zip.file('orders.csv', buildExportCsv(exportOrders));
        const downloadableOrders = exportOrders.filter(o => ['metadata_ready', 'compliance_check', 'pending_verification', 'completed'].includes(o.status));
        if (downloadableOrders.length) {
            showToast.info(`Fetching PDFs for ${downloadableOrders.length} order(s) with documents…`);
            const filesFolder = zip.folder('files');
            let fetched = 0;
            await Promise.all(downloadableOrders.map(async order => {
                try {
                    const response = await fetch(`${API_BASE}/orders/${order.id}/download`, { method: 'GET', credentials: 'include' });
                    if (!response.ok) return;
                    const disposition = response.headers.get('Content-Disposition');
                    const contentType = response.headers.get('Content-Type') || '';
                    const extFromType = contentType.includes('pdf') ? 'pdf'
                        : contentType.includes('png') ? 'png'
                        : contentType.includes('jpeg') || contentType.includes('jpg') ? 'jpg'
                        : contentType.includes('webp') ? 'webp'
                        : 'bin';
                    let filename = `order_${order.id}.${extFromType}`;
                    if (disposition && disposition.includes('filename=')) {
                        filename = disposition.split('filename=')[1].replace(/"/g, '').trim();
                    }
                    filesFolder.file(filename, await response.blob());
                    fetched++;
                } catch { /* skip failed downloads */ }
            }));
            if (fetched === 0) showToast.warning('No files could be fetched — only CSV included in ZIP.');
        } else {
            showToast.warning('No orders with uploaded documents in selection — ZIP will contain only CSV.');
        }
        const content = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders_export_${new Date().toISOString().slice(0, 10)}.zip`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const apiHeaders = () => {
        const key = localStorage.getItem('api_key') || sessionStorage.getItem('api_key') || '';
        return key ? { 'X-API-Key': key } : {};
    };

    const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            if (line[i] === '"') {
                inQuotes = !inQuotes;
            } else if (line[i] === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += line[i];
            }
        }
        result.push(current.trim());
        return result;
    };

    const handleCsvFileSelect = (file) => {
        if (!file) return;
        if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
            setUploadCsvError('Please select a .csv file');
            return;
        }
        setUploadCsvError('');
        setUploadCsvFile(file);

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            setUploadCsvRawText(text);
            const lines = text.split('\n').filter(l => l.trim());
            if (lines.length < 2) {
                setUploadCsvPreviewRows([]);
                setUploadCsvPreviewHeaders([]);
                return;
            }
            const headers = parseCSVLine(lines[0]);
            const rows = lines.slice(1, 6).map(l => parseCSVLine(l));
            setUploadCsvPreviewHeaders(headers);
            setUploadCsvPreviewRows(rows);
        };
        reader.readAsText(file);
    };

    const handleCsvDrop = (e) => {
        e.preventDefault();
        setUploadCsvDragging(false);
        const file = e.dataTransfer.files[0];
        handleCsvFileSelect(file);
    };

    const resetUploadCsvModal = () => {
        setUploadCsvFile(null);
        setUploadCsvPreviewRows(null);
        setUploadCsvPreviewHeaders([]);
        setUploadCsvError('');
        setUploadCsvSubmitting(false);
        setUploadCsvResult(null);
        setUploadCsvRawText('');
    };

    const downloadCsvErrorReport = () => {
        if (!uploadCsvResult || !uploadCsvRawText) return;
        const lines = uploadCsvRawText.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim());
        if (!lines.length) return;

        const errorMap = {};
        (uploadCsvResult.failed_orders || []).forEach(f => {
            // row numbers are 1-based where row 1 = header, data starts at row 2
            errorMap[f.row] = f.error || '';
        });

        const escapeCell = (val) => {
            const s = val === undefined || val === null ? '' : String(val);
            return s.includes(',') || s.includes('"') || s.includes('\n')
                ? `"${s.replace(/"/g, '""')}"` : s;
        };

        const output = lines.map((line, idx) => {
            if (idx === 0) return line + ',error';
            const rowNum = idx + 1; // header is idx=0 → rowNum=1, first data row idx=1 → rowNum=2
            return line + ',' + escapeCell(errorMap[rowNum] || '');
        });

        const blob = new Blob([output.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `error_report_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const openUploadCsvModal = (service) => {
        setUploadCsvService(service);
        resetUploadCsvModal();
    };

    const submitUploadCsv = async () => {
        if (!uploadCsvService || !uploadCsvFile) return;
        setUploadCsvSubmitting(true);
        setUploadCsvError('');
        try {
            const formData = new FormData();
            formData.append('service_id', uploadCsvService.id);
            formData.append('file', uploadCsvFile);

            const response = await fetch(`${API_BASE}/bulk-request`, {
                method: 'POST',
                credentials: 'include',
                headers: apiHeaders(),
                body: formData
            });

            const data = await response.json();
            if (!response.ok) {
                setUploadCsvError(data.detail || 'Upload failed');
                return;
            }

            setUploadCsvResult(data);
            setUploadCsvFile(null);
            setUploadCsvPreviewRows(null);
            setUploadCsvPreviewHeaders([]);
            await fetchOrders();
        } catch (err) {
            console.error('Upload CSV failed:', err);
            setUploadCsvError('Network error - please try again');
        } finally {
            setUploadCsvSubmitting(false);
        }
    };

    const openServiceCsvUploads = async (service) => {
        setViewCsvService(service);
        setServiceCsvUploads([]);
        setServiceCsvUploadsLoading(true);
        setServiceCsvUploadsError('');
        try {
            const response = await fetch(`${API_BASE}/services/${service.id}/csv-uploads?limit=200&offset=0`, {
                method: 'GET',
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to load CSV uploads');
            }
            setServiceCsvUploads(data.uploads || []);
        } catch (err) {
            console.error('Failed to load service CSV uploads:', err);
            setServiceCsvUploadsError(err.message || 'Failed to load CSV uploads');
        } finally {
            setServiceCsvUploadsLoading(false);
        }
    };

    const downloadServiceCsvUpload = async (serviceId, uploadId, suggestedFilename) => {
        try {
            const response = await fetch(`${API_BASE}/services/${serviceId}/csv-uploads/${uploadId}/download`, {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.detail || 'Failed to download CSV');
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = suggestedFilename || `csv_upload_${uploadId}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download uploaded CSV:', err);
            showToast.error(err.message || 'Failed to download uploaded CSV');
        }
    };

    const formatUserLocalDateTime = (value) => formatDateTimeIST(value);

    const applyCsvOrderFilter = async (uploadItem) => {
        const nextFilters = {
            ...orderFilters,
            source_csv_upload_id: uploadItem.id
        };
        setActiveCsvFilter(uploadItem);
        setPendingFilters(prev => ({ ...prev, source_csv_upload_id: uploadItem.id }));
        setOrderFilters(nextFilters);
        setViewCsvService(null);
        await fetchOrders(1, nextFilters);
    };

    const clearCsvOrderFilter = async () => {
        const nextFilters = {
            ...orderFilters,
            source_csv_upload_id: ''
        };
        setActiveCsvFilter(null);
        setPendingFilters(prev => ({ ...prev, source_csv_upload_id: '' }));
        setOrderFilters(nextFilters);
        await fetchOrders(1, nextFilters);
    };

    const handleDownloadTemplate = async (serviceId, serviceName = 'service', serviceState = 'state') => {
        try {
            const response = await fetch(`${API_BASE}/services/${serviceId}/csv-template`, {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to download template');
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const safeName = serviceName.replace(/\s+/g, '_').toLowerCase();
            const safeState = serviceState.replace(/\s+/g, '_').toLowerCase();
            a.download = `${safeName}_${safeState}_template.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Template download failed:', err);
            showToast.error('Failed to download template');
        }
    };


    const openService = (svc) => {
        setSelectedService(svc);
        setRecentServiceIds(prev => {
            const updated = [svc.id, ...prev.filter(id => id !== svc.id)].slice(0, 5);
            localStorage.setItem('ls_recentServices', JSON.stringify(updated));
            return updated;
        });
    };

    const toggleServicePin = (svcId) => {
        setPinnedServiceIds(prev => {
            const next = new Set(prev);
            if (next.has(svcId)) next.delete(svcId);
            else next.add(svcId);
            localStorage.setItem('ls_pinnedServices', JSON.stringify([...next]));
            return next;
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-500">Loading Live Stamping...</span>
            </div>
        );
    }

    // Get recent orders for Client view
    const recentOrders = user.role === 'client'
        ? orders.slice(0, 5) // Assuming orders are returned sorted by date desc, otherwise need sort
        : [];
    const selectedVisibleCount = visibleOrders.filter(o => selectedOrderIds.has(o.id)).length;
    const exportButtonLabel = selectedVisibleCount > 0 ? 'Export selected' : 'Export all';

    return (
        <div className="relative flex flex-col h-full gap-1.5">
            {user.role !== 'vendor' && (
                <div className="flex-shrink-0">
                    {user.role === 'admin' ? (
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            <LiveStampingServiceConfig
                                services={services}
                                onToggle={toggleService}
                                onAdd={addService}
                                onUpdate={updateService}
                                onDelete={deleteService}
                                onView={(svc) => setSelectedService(svc)}
                                onDownloadTemplate={handleDownloadTemplate}
                                onUploadCsv={openUploadCsvModal}
                                onViewCsvUploads={openServiceCsvUploads}
                            />
                        </div>
                    ) : (() => {
                        // Client services table with search / filter / sort / pagination
                        const uniqueStates = [...new Set(services.map(s => s.state).filter(Boolean))].sort();
                        const stampDuty = s => s.stamps?.reduce((sum, st) => sum + ((st.default_values?.denomination || 0) * (st.default_values?.quantity || 1)), 0) || 0;
                        const q = svcSearch.toLowerCase();

                        let filtered = services
                            .filter(s => !svcActiveOnly || s.isEnabled)
                            .filter(s => !svcStateFilter || s.state === svcStateFilter)
                            .filter(s => !svcSearch ||
                                s.name.toLowerCase().includes(q) ||
                                (s.state || '').toLowerCase().includes(q) ||
                                String(s.id).includes(svcSearch) ||
                                (s.client_name || '').toLowerCase().includes(q));

                        if (svcSort === 'name_asc') filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
                        if (svcSort === 'name_desc') filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
                        if (svcSort === 'price_asc') filtered = [...filtered].sort((a, b) => stampDuty(a) - stampDuty(b));
                        if (svcSort === 'price_desc') filtered = [...filtered].sort((a, b) => stampDuty(b) - stampDuty(a));
                        // Pinned services always sort to top
                        filtered = filtered.sort((a, b) => {
                            const ap = pinnedServiceIds.has(a.id), bp = pinnedServiceIds.has(b.id);
                            if (ap && !bp) return -1;
                            if (!ap && bp) return 1;
                            return 0;
                        });
                        const recentFiltered = filtered.filter(s => recentServiceIds.includes(s.id) && !pinnedServiceIds.has(s.id));

                        const totalPages = Math.ceil(filtered.length / SVC_PAGE_SIZE) || 1;
                        const safePage = Math.min(svcPage, totalPages);
                        const paginated = filtered.slice((safePage - 1) * SVC_PAGE_SIZE, safePage * SVC_PAGE_SIZE);

                        return (
                            <div className="flex flex-col bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                                    <div className="flex items-center gap-1.5">
                                        <button onClick={() => setSvcTableCollapsed(c => !c)} className="p-0.5 rounded hover:bg-gray-200 transition-colors" title={svcTableCollapsed ? 'Expand' : 'Collapse'}>
                                            <svg className={`w-3.5 h-3.5 text-gray-500 transition-transform ${svcTableCollapsed ? '-rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                        <h3 className="text-sm font-bold text-gray-800">Available Services</h3>
                                    </div>
                                    <span className="text-xs text-gray-500">{filtered.length} service{filtered.length !== 1 ? 's' : ''}</span>
                                </div>
                                {!svcTableCollapsed && <><div className="flex-shrink-0 px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-1.5 items-end">
                                    <div className="flex flex-col">
                                        <label className="text-xs text-gray-500 mb-0.5">Search</label>
                                        <input
                                            type="text"
                                            value={svcSearch}
                                            onChange={e => { setSvcSearch(e.target.value); setSvcPage(1); }}
                                            placeholder="Name, ID, state..."
                                            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white w-44"
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-xs text-gray-500 mb-0.5">State</label>
                                        <select value={svcStateFilter} onChange={e => { setSvcStateFilter(e.target.value); setSvcPage(1); }} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                                            <option value="">All states</option>
                                            {uniqueStates.map(st => <option key={st} value={st}>{st}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-xs text-gray-500 mb-0.5">Sort</label>
                                        <select value={svcSort} onChange={e => setSvcSort(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                                            <option value="name_asc">Name A-Z</option>
                                            <option value="name_desc">Name Z-A</option>
                                            <option value="price_asc">Price Low-High</option>
                                            <option value="price_desc">Price High-Low</option>
                                        </select>
                                    </div>
                                    <label className="flex items-center gap-1.5 self-end pb-1 cursor-pointer select-none">
                                        <input type="checkbox" checked={svcActiveOnly} onChange={e => { setSvcActiveOnly(e.target.checked); setSvcPage(1); }} className="rounded border-gray-300 text-blue-600" />
                                        <span className="text-sm text-gray-600">Active only</span>
                                    </label>
                                    {(svcSearch || svcStateFilter) && (
                                        <button onClick={() => { setSvcSearch(''); setSvcStateFilter(''); setSvcPage(1); }} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 self-end">Clear</button>
                                    )}
                                </div>
                                <div className="flex-1 overflow-auto min-h-0">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100 sticky top-0">
                                            <tr>
                                                <th className="px-3 py-1 text-left font-semibold">ID</th>
                                                <th className="px-3 py-1 text-left font-semibold">Service Name</th>
                                                <th className="px-3 py-1 text-left font-semibold">State</th>
                                                <th className="px-3 py-1 text-left font-semibold">Stamp Duty</th>
                                                <th className="px-3 py-1 text-left font-semibold">Status</th>
                                                <th className="px-3 py-1 text-center font-semibold">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {recentFiltered.length > 0 && !svcSearch && !svcStateFilter && (
                                                <tr className="bg-amber-50">
                                                    <td colSpan="6" className="px-3 py-0.5 text-xs text-amber-600 font-semibold uppercase tracking-wide">Recent</td>
                                                </tr>
                                            )}
                                            {paginated.map(svc => {
                                                const isPinned = pinnedServiceIds.has(svc.id);
                                                const isRecent = recentServiceIds.includes(svc.id) && !isPinned;
                                                const rowBg = isPinned ? 'bg-indigo-50' : isRecent ? 'bg-amber-50/50' : '';
                                                return (
                                                    <tr key={svc.id} onClick={() => openService(svc)} className={`hover:bg-indigo-50 transition-colors cursor-pointer ${rowBg} ${!svc.isEnabled ? 'opacity-60' : ''}`}>
                                                        <td className="px-3 py-1 text-xs font-mono text-gray-500 whitespace-nowrap">{svc.id}</td>
                                                        <td className="px-3 py-1 text-xs font-medium text-gray-900">
                                                            <div className="flex items-center gap-1">
                                                                {isPinned && <svg className="w-3 h-3 text-indigo-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M16 3a1 1 0 011 1v1h1a1 1 0 010 2h-.17l-.33 6H17a3 3 0 01-3 3H10a3 3 0 01-3-3h-.5l-.33-6H6a1 1 0 010-2h1V4a1 1 0 011-1h8zM9 18a1 1 0 001 1h4a1 1 0 001-1v-1H9v1z"/></svg>}
                                                                <span>{svc.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-1"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded uppercase">{svc.state}</span></td>
                                                        <td className="px-3 py-1 text-xs font-semibold text-gray-800">₹{stampDuty(svc).toLocaleString('en-IN')}</td>
                                                        <td className="px-3 py-1">
                                                            {svc.isEnabled
                                                                ? <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Active</span>
                                                                : <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full">Inactive</span>}
                                                        </td>
                                                        <td className="px-3 py-1 text-center">
                                                            <button
                                                                onClick={e => { e.stopPropagation(); toggleServicePin(svc.id); }}
                                                                className={`p-1.5 rounded-lg transition-colors ${isPinned ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                                                                title={isPinned ? 'Unpin' : 'Pin to top'}
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    openUploadCsvModal(svc);
                                                                }}
                                                                className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                                                                title="Upload CSV"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v8" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    handleDownloadTemplate(svc.id, svc.name, svc.state || 'state');
                                                                }}
                                                                className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
                                                                title="Download CSV template"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 18h16" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    openServiceCsvUploads(svc);
                                                                }}
                                                                className="p-1.5 text-gray-400 hover:text-violet-600 rounded-lg hover:bg-violet-50 transition-colors"
                                                                title="View CSV uploads"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h7" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l7 6-7 6" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h8a2 2 0 012 2v2" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19h8a2 2 0 002-2v-2" />
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {paginated.length === 0 && (
                                                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400 italic">No services found</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <div ref={servicesScrollRef} className="py-2 text-center">
                                        {servicesPagination.loading && <span className="text-xs text-gray-400">Loading...</span>}
                                    </div>
                                </div>
                                {(() => {
                                    const start = filtered.length === 0 ? 0 : (safePage - 1) * SVC_PAGE_SIZE + 1;
                                    const end = Math.min(safePage * SVC_PAGE_SIZE, filtered.length);
                                    return (
                                        <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
                                            <span>Showing {start} to {end} of {filtered.length} service{filtered.length !== 1 ? 's' : ''}</span>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => setSvcPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                                </button>
                                                <span>Go to page:</span>
                                                <input
                                                    type="number" min="1" max={totalPages}
                                                    value={svcGoToPage}
                                                    onChange={e => setSvcGoToPage(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') { const p = Math.min(Math.max(1, Number(svcGoToPage)), totalPages); setSvcPage(p); setSvcGoToPage(''); } }}
                                                    placeholder={String(safePage)}
                                                    className="w-10 text-center text-xs border border-gray-300 rounded px-1 py-0.5"
                                                />
                                                <span>/ {totalPages}</span>
                                                <button onClick={() => setSvcPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}
                                </>}
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* 2. MIDDLE: MAIN CONTENT */}
            <div className="flex-1 overflow-hidden flex flex-col">

                {/* REQUESTS VIEW (Admin/Vendor) */}
                {user.role !== 'client' && (
                    <div className="flex flex-col h-full overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-sm font-bold text-gray-800">All Live Stamping Requests</h3>
                            <div className="flex items-center gap-2">
                                {visibleOrders.length > 0 && (
                                    <div className="relative" ref={exportMenuRef}>
                                        <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden">
                                            <button onClick={exportSelectedOrders} className="px-2 py-1 text-xs font-medium bg-white hover:bg-gray-50">{exportButtonLabel}</button>
                                            <button onClick={() => setShowExportMenu(v => !v)} className="px-1.5 bg-gray-50 hover:bg-gray-100 border-l border-gray-300 text-gray-500">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                            </button>
                                        </div>
                                        {showExportMenu && (
                                            <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                                                <button onClick={() => { exportSelectedOrders(); setShowExportMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                    Export CSV
                                                </button>
                                                <button onClick={() => { exportZipWithPdfs(); setShowExportMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100">
                                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                                    Export ZIP (PDFs + CSV)
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {user.role === 'admin' && (
                                    <button
                                        onClick={async () => {
                                            setShowTransactionsBlade(true);
                                            await fetchPaymentRequests();
                                        }}
                                        className="px-2 py-1 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Transactions
                                    </button>
                                )}
                            </div>
                        </div>
                        {activeCsvFilter && (
                            <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100 bg-blue-50 flex items-center justify-between">
                                <span className="text-sm text-blue-700">
                                    Showing orders created from CSV: <span className="font-semibold">{activeCsvFilter.original_filename || activeCsvFilter.storage_filename}</span>
                                </span>
                                <button
                                    onClick={clearCsvOrderFilter}
                                    className="px-2.5 py-1 text-xs font-medium border border-blue-200 text-blue-700 rounded hover:bg-blue-100"
                                >
                                    Remove filter
                                </button>
                            </div>
                        )}
                        {/* Filter bar */}
                        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-100 px-3 py-1.5 flex flex-wrap gap-1.5 items-end">
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">Search</label>
                                <input type="text" value={orderSearch} onChange={e => setOrderSearch(e.target.value)} placeholder="ID, ref, service, client, vendor, state, article, party…" className="text-sm border border-gray-300 rounded px-2 py-1 bg-white w-56" />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">Status</label>
                                <select value={pendingFilters.status} onChange={e => setPendingFilters(f => ({ ...f, status: e.target.value }))} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                                    <option value="">All</option>
                                    {['placed','executing','preview_submitted','metadata_ready','compliance_check','completed','rejected','payment_pending'].map(s => (
                                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">Client</label>
                                <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white max-w-[160px]">
                                    <option value="">All</option>
                                    {[...new Set(orders.map(o => o.client_name).filter(Boolean))].sort().map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">Service</label>
                                <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white max-w-[160px]">
                                    <option value="">All</option>
                                    {[...new Set(services.map(s => s.name).filter(Boolean))].sort().map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">From</label>
                                <input type="date" value={pendingFilters.from_date} onChange={e => setPendingFilters(f => ({ ...f, from_date: e.target.value }))} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white" />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">To</label>
                                <input type="date" value={pendingFilters.to_date} onChange={e => setPendingFilters(f => ({ ...f, to_date: e.target.value }))} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white" />
                            </div>
                            <button onClick={() => { setOrderFilters(pendingFilters); fetchOrders(1, pendingFilters, orderSearch, clientFilter, serviceFilter); }} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Apply</button>
                            <button onClick={() => { setOrderFilters(emptyFilters); setPendingFilters(emptyFilters); setOrderSearch(''); setClientFilter(''); setServiceFilter(''); fetchOrders(1, emptyFilters, '', '', ''); }} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Clear</button>
                        </div>
                        <div className="flex-1 overflow-auto min-h-0">
                            <table className="w-full">
                                <thead className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100 sticky top-0 bg-white">
                                    <tr>
                                        <th className="px-3 py-1 text-left font-semibold">
                                            <input
                                                type="checkbox"
                                                checked={visibleOrders.length > 0 && visibleOrders.every(o => selectedOrderIds.has(o.id))}
                                                onChange={toggleSelectAllVisibleOrders}
                                            />
                                        </th>
                                        <th className="px-3 py-1 text-left font-semibold">Order ID</th>
                                        <th className="px-3 py-1 text-left font-semibold">Ref Id</th>
                                        <th className="px-3 py-1 text-left font-semibold">Service</th>
                                        <th className="px-3 py-1 text-left font-semibold">Client</th>
                                        <th className="px-3 py-1 text-left font-semibold">Status</th>
                                        <th className="px-3 py-1 text-left font-semibold">Created</th>
                                        <th className="px-2 py-1 text-center font-semibold w-[180px] min-w-[180px]">Deliverables</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {visibleOrders.map(order => (
                                        <tr key={order.id} onClick={() => setSelectedOrder(order)} className="odd:bg-white even:bg-gray-50 hover:bg-indigo-50 transition-colors cursor-pointer">
                                            <td className="px-3 py-1 text-sm" onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedOrderIds.has(order.id)}
                                                    onChange={() => toggleOrderSelection(order.id)}
                                                />
                                            </td>
                                            <td className="px-3 py-1 text-sm font-medium text-blue-600 max-w-[120px] overflow-hidden">
                                                <CopyableText text={order.id} className="block truncate font-medium text-blue-600 max-w-full">#{order.id}</CopyableText>
                                            </td>
                                            <td className="px-3 py-1 text-sm text-gray-500 max-w-[110px] overflow-hidden">
                                                {order.external_ref_id
                                                    ? <CopyableText text={order.external_ref_id} className="block truncate text-gray-500 max-w-full">{order.external_ref_id}</CopyableText>
                                                    : '—'}
                                            </td>
                                            <td className="px-3 py-1 text-sm text-gray-700">{order.service_name || order.serviceName}</td>
                                            <td className="px-3 py-1 text-sm text-gray-600">{order.client_name}</td>
                                            <td className="px-3 py-1">
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                                                    {order.status.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className="px-3 py-1 text-sm text-gray-500">{formatISTDateOnly(order.created_at)}</td>
                                            <td className="px-2 py-1 w-[180px] min-w-[180px]" onClick={e => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1">
                                                    {/* Left slot — secondary action (Final Doc / Download) */}
                                                    <div className="w-[68px] flex justify-center">
                                                        {user.role === 'vendor' && order.status === 'metadata_ready' && (
                                                            <button onClick={() => setUploadOrder(order)} className="px-1.5 py-0.5 text-xs text-teal-700 bg-teal-50 rounded hover:bg-teal-100 w-full text-center">Final Doc</button>
                                                        )}
                                                        {user.role === 'admin' && order.status === 'metadata_ready' && (
                                                            <button onClick={() => setUploadOrder(order)} className="px-1.5 py-0.5 text-xs text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100 w-full text-center">Final Doc</button>
                                                        )}
                                                        {order.status === 'completed' && (
                                                            <button onClick={() => handleDownloadDocument(order.id)} className="px-1.5 py-0.5 text-xs text-blue-700 bg-blue-50 rounded hover:bg-blue-100 w-full text-center">Download</button>
                                                        )}
                                                    </div>
                                                    {/* Right slot — primary action (Review / Preview) */}
                                                    <div className="w-[58px] flex justify-center">
                                                        {user.role === 'vendor' && order.status === 'executing' && (
                                                            <button onClick={() => setPreviewUploadOrder(order)} className="px-1.5 py-0.5 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100 w-full text-center">Preview</button>
                                                        )}
                                                        {user.role === 'admin' && order.status === 'executing' && (
                                                            <button onClick={() => setPreviewUploadOrder(order)} className="px-1.5 py-0.5 text-xs text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100 w-full text-center">Preview</button>
                                                        )}
                                                        {user.role === 'admin' && ['preview_submitted', 'compliance_check', 'pending_verification'].includes(order.status) && (
                                                            <button onClick={() => setReviewOrder(order)} className="px-1.5 py-0.5 text-xs text-violet-700 bg-violet-50 rounded hover:bg-violet-100 font-medium w-full text-center">Approve</button>
                                                        )}
                                                        {user.role === 'admin' && order.status === 'metadata_ready' && (
                                                            <button onClick={() => setReviewOrder(order)} className="px-1.5 py-0.5 text-xs text-violet-700 bg-violet-50 rounded hover:bg-violet-100 font-medium w-full text-center">Preview</button>
                                                        )}
                                                    </div>
                                                    <button onClick={() => setSelectedOrder(order)} className="p-1 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="View details">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination Controls */}
                        {(() => {
                            const totalPages = Math.ceil(ordersPagination.total / ordersPagination.limit) || 1;
                            const start = ordersPagination.total === 0 ? 0 : (ordersPagination.page - 1) * ordersPagination.limit + 1;
                            const end = Math.min(ordersPagination.page * ordersPagination.limit, ordersPagination.total);
                            return (
                                <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
                                    <span>Showing {start} to {end} of {ordersPagination.total} orders</span>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => fetchOrders(ordersPagination.page - 1, orderFilters, orderSearch, clientFilter, serviceFilter)} disabled={ordersPagination.page <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        <span>Go to page:</span>
                                        <input
                                            type="number" min="1" max={totalPages}
                                            value={goToPageInput}
                                            onChange={e => setGoToPageInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { const p = Math.min(Math.max(1, Number(goToPageInput)), totalPages); fetchOrders(p, orderFilters, orderSearch, clientFilter, serviceFilter); setGoToPageInput(''); } }}
                                            placeholder={String(ordersPagination.page)}
                                            className="w-10 text-center text-xs border border-gray-300 rounded px-1 py-0.5"
                                        />
                                        <span>/ {totalPages}</span>
                                        <button onClick={() => fetchOrders(ordersPagination.page + 1, orderFilters, orderSearch, clientFilter, serviceFilter)} disabled={ordersPagination.page >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}


                {/* CLIENT REQUESTS VIEW - Full Table with Pagination */}
                {user.role === 'client' && (
                    <div className="flex flex-col h-full overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 flex justify-between items-center flex-wrap gap-1.5">
                            <div className="flex items-center gap-3 flex-wrap">
                                <h3 className="font-bold text-gray-800">My Stamp Requests</h3>
                                <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                        {ordersPagination.total} Total
                                    </span>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        {orderStatusCounts['completed'] || 0} Completed
                                    </span>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        {Object.entries(orderStatusCounts).filter(([s]) => !['completed','rejected'].includes(s)).reduce((sum,[,c])=>sum+c,0)} In Progress
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {visibleOrders.length > 0 && (
                                    <div className="relative" ref={exportMenuRef}>
                                        <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden">
                                            <button onClick={exportSelectedOrders} className="px-2 py-1 text-xs font-medium bg-white hover:bg-gray-50">{exportButtonLabel}</button>
                                            <button onClick={() => setShowExportMenu(v => !v)} className="px-1.5 bg-gray-50 hover:bg-gray-100 border-l border-gray-300 text-gray-500">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                            </button>
                                        </div>
                                        {showExportMenu && (
                                            <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                                                <button onClick={() => { exportSelectedOrders(); setShowExportMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                    Export CSV
                                                </button>
                                                <button onClick={() => { exportZipWithPdfs(); setShowExportMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100">
                                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                                    Export ZIP (PDFs + CSV)
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="relative" ref={balanceMenuRef}>
                                    <div className="flex items-stretch rounded-xl shadow-sm overflow-hidden">
                                        <button
                                            onClick={() => setShowDepositModal(true)}
                                            className="flex items-center bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors"
                                        >
                                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" /></svg>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold">₹{walletBalance.toLocaleString('en-IN')}</span>
                                        </div>
                                        <svg className="w-4 h-4 ml-2 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                        </button>
                                        <button
                                            onClick={() => setShowBalanceMenu(prev => !prev)}
                                            className="bg-blue-700 text-white px-2 hover:bg-blue-800 transition-colors border-l border-blue-500"
                                            aria-label="Open balance menu"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                    </div>
                                    {showBalanceMenu && (
                                        <div className="absolute top-full right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                                            <button
                                                onClick={async () => {
                                                    setShowBalanceMenu(false);
                                                    setShowTransactionsBlade(true);
                                                    await fetchPaymentRequests();
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                                            >
                                                Transactions
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {activeCsvFilter && (
                            <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100 bg-blue-50 flex items-center justify-between">
                                <span className="text-sm text-blue-700">
                                    Showing orders created from CSV: <span className="font-semibold">{activeCsvFilter.original_filename || activeCsvFilter.storage_filename}</span>
                                </span>
                                <button
                                    onClick={clearCsvOrderFilter}
                                    className="px-2.5 py-1 text-xs font-medium border border-blue-200 text-blue-700 rounded hover:bg-blue-100"
                                >
                                    Remove filter
                                </button>
                            </div>
                        )}
                        {/* Filter bar */}
                        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-100 px-3 py-1.5 flex flex-wrap gap-1.5 items-end">
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">Search</label>
                                <input type="text" value={orderSearch} onChange={e => setOrderSearch(e.target.value)} placeholder="ID, ref, service, state, article, party…" className="text-sm border border-gray-300 rounded px-2 py-1 bg-white w-56" />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">Status</label>
                                <select value={pendingFilters.status} onChange={e => setPendingFilters(f => ({ ...f, status: e.target.value }))} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                                    <option value="">All</option>
                                    {['placed','executing','preview_submitted','metadata_ready','compliance_check','completed','rejected','payment_pending'].map(s => (
                                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">Service</label>
                                <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white max-w-[160px]">
                                    <option value="">All</option>
                                    {[...new Set(services.map(s => s.name).filter(Boolean))].sort().map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">From</label>
                                <input type="date" value={pendingFilters.from_date} onChange={e => setPendingFilters(f => ({ ...f, from_date: e.target.value }))} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white" />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-500 mb-0.5">To</label>
                                <input type="date" value={pendingFilters.to_date} onChange={e => setPendingFilters(f => ({ ...f, to_date: e.target.value }))} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white" />
                            </div>
                            <button onClick={() => { setOrderFilters(pendingFilters); fetchOrders(1, pendingFilters, orderSearch, clientFilter, serviceFilter); }} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Apply</button>
                            <button onClick={() => { setOrderFilters(emptyFilters); setPendingFilters(emptyFilters); setOrderSearch(''); setServiceFilter(''); fetchOrders(1, emptyFilters, '', '', ''); }} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Clear</button>
                        </div>
                        <div className="flex-1 overflow-auto min-h-0">
                            <table className="w-full">
                                <thead className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100 sticky top-0 bg-white">
                                    <tr>
                                        <th className="px-3 py-1 text-left font-semibold">
                                            <input
                                                type="checkbox"
                                                checked={visibleOrders.length > 0 && visibleOrders.every(o => selectedOrderIds.has(o.id))}
                                                onChange={toggleSelectAllVisibleOrders}
                                            />
                                        </th>
                                        <th className="px-3 py-1 text-left font-semibold">Order ID</th>
                                        <th className="px-3 py-1 text-left font-semibold">Ref Id</th>
                                        <th className="px-3 py-1 text-left font-semibold">Service</th>
                                        <th className="px-3 py-1 text-left font-semibold">Stamp Duty</th>
                                        <th className="px-3 py-1 text-left font-semibold">Status</th>
                                        <th className="px-3 py-1 text-left font-semibold">Created</th>
                                        <th className="px-2 py-1 text-center font-semibold w-[180px] min-w-[180px]">Deliverables</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {visibleOrders.map(order => (
                                        <tr key={order.id} onClick={() => setSelectedOrder(order)} className="odd:bg-white even:bg-gray-50 hover:bg-indigo-50 transition-colors cursor-pointer">
                                            <td className="px-3 py-1 text-sm" onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedOrderIds.has(order.id)}
                                                    onChange={() => toggleOrderSelection(order.id)}
                                                />
                                            </td>
                                            <td className="px-3 py-1 text-sm font-medium text-blue-600 max-w-[120px] overflow-hidden">
                                                <CopyableText text={order.id} className="block truncate font-medium text-blue-600 max-w-full">#{order.id}</CopyableText>
                                            </td>
                                            <td className="px-3 py-1 text-sm text-gray-500 max-w-[110px] overflow-hidden">
                                                {order.external_ref_id
                                                    ? <CopyableText text={order.external_ref_id} className="block truncate text-gray-500 max-w-full">{order.external_ref_id}</CopyableText>
                                                    : '—'}
                                            </td>
                                            <td className="px-3 py-1 text-sm text-gray-700">{order.service_name || order.serviceName}</td>
                                            <td className="px-3 py-1 text-sm text-gray-700 font-medium">₹{((order.stamps || []).reduce((sum, s) => sum + ((s.denomination || 0) * (s.quantity || 1)), 0)).toLocaleString('en-IN')}</td>
                                            <td className="px-3 py-1">
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                                                    {order.status.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className="px-3 py-1 text-sm text-gray-500">{formatISTDateOnly(order.created_at)}</td>
                                            <td className="px-2 py-1 w-[140px] min-w-[140px]" onClick={e => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1">
                                                    {/* Left slot — secondary action (Download) */}
                                                    <div className="w-[68px] flex justify-center">
                                                        {order.status === 'completed' && (
                                                            <button onClick={() => handleDownloadDocument(order.id)} className="px-1.5 py-0.5 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100 w-full text-center">Download</button>
                                                        )}
                                                    </div>
                                                    {/* Right slot — primary action (Preview) */}
                                                    <div className="w-[58px] flex justify-center">
                                                        {order.status === 'metadata_ready' && (
                                                            <button onClick={() => setReviewOrder(order)} className="px-1.5 py-0.5 text-xs text-violet-700 bg-violet-50 rounded hover:bg-violet-100 font-medium w-full text-center">Preview</button>
                                                        )}
                                                    </div>
                                                    <button onClick={() => setSelectedOrder(order)} className="p-1 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="View details">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {visibleOrders.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-8 text-center text-gray-400 italic">No requests found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination Controls */}
                        {(() => {
                            const totalPages = Math.ceil(ordersPagination.total / ordersPagination.limit) || 1;
                            const hasLocalFilter = !!(orderSearch || serviceFilter);
                            const start = ordersPagination.total === 0 ? 0 : (ordersPagination.page - 1) * ordersPagination.limit + 1;
                            const end = Math.min(ordersPagination.page * ordersPagination.limit, ordersPagination.total);
                            return (
                                <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
                                    <span>
                                        {hasLocalFilter
                                            ? `${visibleOrders.length} of ${ordersPagination.total} orders match filter`
                                            : `Showing ${start} to ${end} of ${ordersPagination.total} orders`}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => fetchOrders(ordersPagination.page - 1, orderFilters, orderSearch, clientFilter, serviceFilter)} disabled={ordersPagination.page <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        <span>Go to page:</span>
                                        <input
                                            type="number" min="1" max={totalPages}
                                            value={goToPageInput}
                                            onChange={e => setGoToPageInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { const p = Math.min(Math.max(1, Number(goToPageInput)), totalPages); fetchOrders(p, orderFilters, orderSearch, clientFilter, serviceFilter); setGoToPageInput(''); } }}
                                            placeholder={String(ordersPagination.page)}
                                            className="w-10 text-center text-xs border border-gray-300 rounded px-1 py-0.5"
                                        />
                                        <span>/ {totalPages}</span>
                                        <button onClick={() => fetchOrders(ordersPagination.page + 1, orderFilters, orderSearch, clientFilter, serviceFilter)} disabled={ordersPagination.page >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

            </div>

            <Modal isOpen={showTransactionsBlade} onClose={() => setShowTransactionsBlade(false)}>
                <div className="flex flex-col h-full">
                    <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex items-center gap-3">
                        <button
                            onClick={() => setShowTransactionsBlade(false)}
                            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
                            aria-label="Back"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <h2 className="text-xl font-bold text-gray-900">Transactions</h2>
                    </div>
                    <div className="flex-1 overflow-auto p-6 bg-gray-50">
                        <LiveStampingPaymentJournal
                            requests={paymentRequests}
                            loading={false}
                            isAdmin={user.role === 'admin'}
                            onApprove={handleApproveRequest}
                            onReject={handleRejectRequest}
                        />
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 bg-white flex-shrink-0 flex justify-end">
                        <button
                            onClick={() => setShowTransactionsBlade(false)}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={!!uploadCsvService} onClose={() => setUploadCsvService(null)}>
                <div className="flex flex-col h-full">
                    <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex items-center gap-3">
                        <button
                            onClick={() => { setUploadCsvService(null); resetUploadCsvModal(); }}
                            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
                            aria-label="Back"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Upload CSV</h2>
                            {uploadCsvService && (
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {uploadCsvService.name} - {uploadCsvService.state}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto p-6 bg-gray-50 space-y-4 flex flex-col">
                        {uploadCsvResult ? (
                            /* ── Result view ── */
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                                        <p className="text-2xl font-bold text-gray-800">{uploadCsvResult.total_submitted}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Submitted</p>
                                    </div>
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                                        <p className="text-2xl font-bold text-green-700">{uploadCsvResult.total_created}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Created</p>
                                    </div>
                                    <div className={`border rounded-xl p-3 text-center ${uploadCsvResult.total_failed > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                                        <p className={`text-2xl font-bold ${uploadCsvResult.total_failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{uploadCsvResult.total_failed}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Failed</p>
                                    </div>
                                </div>

                                {uploadCsvResult.created_orders?.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-xs font-semibold text-green-700 uppercase tracking-wide">Created Orders</div>
                                        <div className="overflow-x-auto">
                                            <table className="text-xs w-full">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-gray-600 font-medium">Row</th>
                                                        <th className="px-3 py-2 text-left text-gray-600 font-medium">Order ID</th>
                                                        <th className="px-3 py-2 text-left text-gray-600 font-medium">Ref Id</th>
                                                        <th className="px-3 py-2 text-left text-gray-600 font-medium">Status</th>
                                                        <th className="px-3 py-2 text-left text-gray-600 font-medium">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {uploadCsvResult.created_orders.map((o, i) => (
                                                        <tr key={i} className="border-t hover:bg-gray-50">
                                                            <td className="px-3 py-2 text-gray-500">{o.row}</td>
                                                            <td className="px-3 py-2 font-mono text-indigo-600">{o.order_id}</td>
                                                            <td className="px-3 py-2 text-gray-600">{o.external_ref_id || '—'}</td>
                                                            <td className="px-3 py-2">
                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${o.status === 'placed' || o.status === 'executing' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                                    {o.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-700">₹{o.amount?.toLocaleString('en-IN')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {uploadCsvResult.failed_orders?.length > 0 && (
                                    <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
                                        <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center justify-between">
                                            <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">Failed Rows</span>
                                            <button
                                                onClick={downloadCsvErrorReport}
                                                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-red-300 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 18h16" />
                                                </svg>
                                                Download Error Report
                                            </button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="text-xs w-full">
                                                <thead className="bg-red-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-red-600 font-medium">Row</th>
                                                        <th className="px-3 py-2 text-left text-red-600 font-medium">Ref Id</th>
                                                        <th className="px-3 py-2 text-left text-red-600 font-medium">Error</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {uploadCsvResult.failed_orders.map((f, i) => (
                                                        <tr key={i} className="border-t hover:bg-red-50">
                                                            <td className="px-3 py-2 text-gray-500">{f.row}</td>
                                                            <td className="px-3 py-2 text-gray-600">{f.external_ref_id || '—'}</td>
                                                            <td className="px-3 py-2 text-red-600">{f.error}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* ── Upload view ── */
                            <>
                                <div className="bg-white border border-gray-200 rounded-xl p-4 flex-1 flex flex-col min-h-[260px]">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Upload file</h3>
                                    <label
                                        onDragOver={e => { e.preventDefault(); setUploadCsvDragging(true); }}
                                        onDragEnter={e => { e.preventDefault(); setUploadCsvDragging(true); }}
                                        onDragLeave={() => setUploadCsvDragging(false)}
                                        onDrop={handleCsvDrop}
                                        className={`flex-1 flex flex-col items-center justify-center cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition-colors ${uploadCsvDragging
                                                ? 'border-indigo-500 bg-indigo-50'
                                                : uploadCsvFile
                                                    ? 'border-green-400 bg-green-50'
                                                    : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30'
                                            }`}
                                    >
                                        <input
                                            type="file"
                                            accept=".csv,text/csv"
                                            onChange={e => handleCsvFileSelect(e.target.files[0])}
                                            className="hidden"
                                        />
                                        <svg className={`w-10 h-10 mb-2 ${uploadCsvDragging ? 'text-indigo-500' : uploadCsvFile ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.9A5 5 0 0115.9 6 4.5 4.5 0 0117 15M12 12v8m0-8l-3 3m3-3l3 3" />
                                        </svg>
                                        {uploadCsvFile ? (
                                            <>
                                                <p className="text-sm font-medium text-green-700">{uploadCsvFile.name}</p>
                                                <p className="text-xs text-gray-500 mt-1">Click or drop another file to replace</p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm font-medium text-gray-700">
                                                    {uploadCsvDragging ? 'Drop the CSV here' : 'Drag & drop CSV here'}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">or <span className="text-indigo-600 font-medium">click to browse</span></p>
                                            </>
                                        )}
                                    </label>
                                </div>

                                {uploadCsvPreviewRows && uploadCsvPreviewRows.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
                                        <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                                            Preview - first {uploadCsvPreviewRows.length} row{uploadCsvPreviewRows.length > 1 ? 's' : ''}
                                        </div>
                                        <table className="text-xs w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    {uploadCsvPreviewHeaders.map((h, i) => (
                                                        <th key={i} className="px-3 py-2 text-left text-gray-600 font-medium">{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {uploadCsvPreviewRows.map((row, ri) => (
                                                    <tr key={ri} className="border-t">
                                                        {row.map((cell, ci) => (
                                                            <td key={ci} className="px-3 py-2 text-gray-700">{cell}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {uploadCsvError && (
                                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                        {uploadCsvError}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-end gap-3">
                        {uploadCsvResult ? (
                            <>
                                <button
                                    onClick={resetUploadCsvModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                                >
                                    Upload Another
                                </button>
                                <button
                                    onClick={() => { setUploadCsvService(null); resetUploadCsvModal(); }}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                                >
                                    Done
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setUploadCsvService(null)}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitUploadCsv}
                                    disabled={!uploadCsvFile || uploadCsvSubmitting}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                                >
                                    {uploadCsvSubmitting ? 'Uploading...' : 'Submit CSV'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal isOpen={!!viewCsvService} onClose={() => setViewCsvService(null)}>
                <div className="flex flex-col h-full">
                    <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex items-center gap-3">
                        <button
                            onClick={() => setViewCsvService(null)}
                            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
                            aria-label="Back"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Uploaded CSVs</h2>
                            {viewCsvService && (
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {viewCsvService.name} - {viewCsvService.state}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto p-6 bg-gray-50">
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Uploaded At</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Original Name</th>
                                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {serviceCsvUploadsLoading && (
                                        <tr>
                                            <td colSpan="3" className="px-4 py-8 text-center text-gray-500">Loading CSV uploads...</td>
                                        </tr>
                                    )}
                                    {!serviceCsvUploadsLoading && serviceCsvUploadsError && (
                                        <tr>
                                            <td colSpan="3" className="px-4 py-8 text-center text-red-600">{serviceCsvUploadsError}</td>
                                        </tr>
                                    )}
                                    {!serviceCsvUploadsLoading && !serviceCsvUploadsError && serviceCsvUploads.length === 0 && (
                                        <tr>
                                            <td colSpan="3" className="px-4 py-8 text-center text-gray-500">No uploaded CSV found for this service.</td>
                                        </tr>
                                    )}
                                    {!serviceCsvUploadsLoading && !serviceCsvUploadsError && serviceCsvUploads.map((item) => (
                                        <tr key={item.id}>
                                            <td className="px-4 py-2.5 text-gray-700">{formatUserLocalDateTime(item.created_at)}</td>
                                            <td className="px-4 py-2.5 text-gray-700">{item.original_filename || '-'}</td>
                                            <td className="px-4 py-2.5 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => applyCsvOrderFilter(item)}
                                                        className="px-3 py-1.5 text-sm font-medium border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50"
                                                    >
                                                        View order
                                                    </button>
                                                    <button
                                                        onClick={() => downloadServiceCsvUpload(item.service_id, item.id, (item.storage_filename || '').split('/').pop())}
                                                        className="px-2 py-1 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                                                    >
                                                        Download
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 bg-white flex-shrink-0 flex justify-end">
                        <button
                            onClick={() => setViewCsvService(null)}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>



            {/* MODALS */}
            {reviewOrder && (
                <AdminReviewModal
                    order={reviewOrder}
                    onClose={() => setReviewOrder(null)}
                    onApprove={handleApproveOrder}
                    onReject={handleRejectOrder}
                />
            )}
            {selectedService && (
                <LiveStampingServiceDetailModal
                    service={selectedService}
                    orders={user.role === 'client' ? orders.filter(o => o.service_id === selectedService.id || o.serviceId === selectedService.id) : []}
                    onClose={() => setSelectedService(null)}
                    onViewOrder={(order) => { setSelectedService(null); setSelectedOrder(order); }}
                    onDelete={user.role === 'admin' ? deleteService : undefined}
                    onRequest={user.role === 'client' ? (svc) => { setSelectedService(null); setRequestService(svc); } : undefined}
                />
            )}

            {/* Manual Deposit Modal */}
            {showDepositModal && (
                <LiveStampingDepositModal
                    onClose={() => setShowDepositModal(false)}
                    onSubmit={handleDepositSubmit}
                />
            )}

            {selectedOrder && <LiveStampingOrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} userRole={user?.role} />}

            {uploadOrder && <LiveStampingUploadModal order={uploadOrder} onClose={() => setUploadOrder(null)} onComplete={handleUploadComplete} />}
            {previewUploadOrder && <VendorSampleUpload order={previewUploadOrder} onSuccess={() => { setPreviewUploadOrder(null); fetchOrders(); }} onClose={() => setPreviewUploadOrder(null)} />}
            {requestService && <LiveStampingRequestModal service={requestService} walletBalance={walletBalance} onClose={() => setRequestService(null)} onSubmit={handlePlaceOrder} />}

        </div>
    );
};
export default LiveStampingPanel;